import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Rating,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { Book } from '../types';

interface BookCardProps {
  book: Book;
  isRtl: boolean;
  onBookClick: (bookId: number) => void;
  onAuthorClick: (authorId: number, event: React.MouseEvent) => void;
  formatNumber: (num: number | string) => string;
}

const BookCard: React.FC<BookCardProps> = ({
  book,
  isRtl,
  onBookClick,
  onAuthorClick,
  formatNumber,
}) => {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        '&:hover': {
          boxShadow: 6,
        },
      }}
    >
      <CardMedia
        component="img"
        sx={{
          height: 300,
          objectFit: 'cover',
        }}
        image={book.cover_image_url.startsWith('http') ? book.cover_image_url : `/images/${book.cover_image_url}`}
        alt={book?.title}
        onClick={() => onBookClick(book?.id || 0)}
        onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = '/images/ktebstan.jpeg';
          }}
      />
      <CardContent
        sx={{
          flexGrow: 1,
          textAlign: !isRtl ? 'right' : 'left',
          direction: !isRtl ? 'rtl' : 'ltr',
        }}
      >
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontSize: '1rem',
            fontWeight: 500,
            mb: 1,
            cursor: 'pointer',
            direction: !isRtl ? 'rtl' : 'ltr',
            textAlign: !isRtl ? 'right' : 'left',
            width: '100%',
            '&:hover': {
              color: 'primary.main',
            },
          }}
          onClick={() => onBookClick(book?.id || 0)}
        >
          {book?.title}
        </Typography>
        
        {/* Author section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 1,
            gap: 0.5,
            flexDirection: !isRtl ? 'row-reverse' : 'row',
            width: '100%',
            justifyContent: !isRtl ? 'flex-start' : 'flex-start',
          }}
        >
          {book?.author?.name && (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
              direction: !isRtl ? 'rtl' : 'ltr',
            }}
          >
            {isRtl ? 'نووسەر: ' : 'by: '}
          </Typography>
          <Typography
            variant="body2"
            component="span"
            onClick={(e) => onAuthorClick(book?.author?.id, e)}
            sx={{
              direction: !isRtl ? 'rtl' : 'ltr',
              textAlign: !isRtl ? 'right' : 'left',
              flex: 1,
              cursor: 'pointer',
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
                {isRtl ? book?.author?.name_ku || book?.author?.name : book?.author?.name}
              </Typography>
            </>
          )}
        </Box>

        {/* Rating section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
            flexDirection: !isRtl ? 'row-reverse' : 'row',
          }}
        >
          <Rating
            value={book?.average_rating}
            precision={0.5}
            readOnly
            size="small"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
            ({book?.rating_count})
          </Typography>
        </Box>

        {/* Stats section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            flexDirection: !isRtl ? 'row-reverse' : 'row',
          }}
        >
          <StatItem icon={<VisibilityIcon fontSize="small" />} value={book?.viewed} formatNumber={formatNumber} />
          <StatItem icon={<CloudDownloadIcon fontSize="small" />} value={book?.download} formatNumber={formatNumber} />
          <StatItem icon={<ThumbUpIcon fontSize="small" />} value={book?.likes} formatNumber={formatNumber} />
        </Box>
      </CardContent>
    </Card>
  );
};

interface StatItemProps {
  icon: React.ReactNode;
  value: number;
  formatNumber: (num: number | string) => string;
}

const StatItem: React.FC<StatItemProps> = ({ icon, value, formatNumber }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
    {icon}
    <Typography variant="body2">{formatNumber(value || 0)}</Typography>
  </Box>
);

export default BookCard; 